import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../context/AuthContext'
import CoordinatorLayout from '../components/CoordinatorLayout'
import LoadingSpinner from '../../../components/LoadingSpinner'

export default function CoordinatorOverview() {
  const { user } = useAuth()
  const [stats, setStats] = useState(null)
  const [recentMembers, setRecentMembers] = useState([])
  const [chapterName, setChapterName] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      const { data: coordinator } = await supabase
        .from('supporters').select('chapter_id, chapters(name)').eq('id', user.id).single()

      if (!coordinator?.chapter_id) { setLoading(false); return }

      const chapterId = coordinator.chapter_id
      setChapterName(coordinator.chapters?.name || '')

      const { count: total } = await supabase.from('supporters').select('*', { count: 'exact', head: true }).eq('chapter_id', chapterId)
      const { count: active } = await supabase.from('supporters').select('*', { count: 'exact', head: true }).eq('chapter_id', chapterId).eq('status', 'active')
      const { count: male } = await supabase.from('supporters').select('*', { count: 'exact', head: true }).eq('chapter_id', chapterId).eq('gender', 'male')
      const { count: female } = await supabase.from('supporters').select('*', { count: 'exact', head: true }).eq('chapter_id', chapterId).eq('gender', 'female')
      const { data: members } = await supabase.from('supporters').select('*').eq('chapter_id', chapterId).order('joined_at', { ascending: false }).limit(5)

      setStats({ total: total || 0, active: active || 0, male: male || 0, female: female || 0 })
      setRecentMembers(members || [])
      setLoading(false)
    }
    fetchData()
  }, [user])

  if (loading) return <LoadingSpinner fullScreen />

  return (
    <CoordinatorLayout>
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-4xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">
          {chapterName} Chapter
        </h1>
        <p className="text-gray-500 text-sm">Your chapter overview and member activity</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-8 lg:mb-10">
        {[
          { label: 'Total Members', value: stats?.total },
          { label: 'Active', value: stats?.active },
          { label: 'Male', value: stats?.male },
          { label: 'Female', value: stats?.female },
        ].map((stat) => (
          <div key={stat.label} className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4 lg:p-6">
            <div className="text-3xl lg:text-4xl font-black text-green-500 dark:text-green-400 mb-2">{stat.value}</div>
            <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Recent Members */}
      <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4 lg:p-6">
        <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-4">Recent Members</div>
        <div className="flex flex-col gap-3">
          {recentMembers.map((member) => (
            <div key={member.id} className="flex justify-between items-center border-b border-gray-200 dark:border-white/5 pb-3">
              <div>
                <div className="font-semibold text-sm text-gray-900 dark:text-white">{member.full_name}</div>
                <div className="text-gray-500 text-xs">{member.membership_id}</div>
              </div>
              <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 ${
                member.status === 'active'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-white/10 text-gray-400'
              }`}>
                {member.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </CoordinatorLayout>
  )
}